import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../api';
import type { ModuleViewProps } from '../../shell/types';
import { useToast } from '../../shell/toast';
import { useTab } from '../../shell/hooks';
import {
  ErrorState,
  LoadingState,
  TableSkeleton,
  ModulePage,
  MoneyInput,
  Table,
  Checkbox,
  Select,
  TextInput,
} from '../../shell/kit';
import { toResourceError, type ResourceError } from '../../shell/hooks';
import { formatMoney, formatMoment } from '../../shell/format';
import {
  cancelSubscription,
  chargeSubscription,
  checkVehicle,
  createPlan,
  fetchChurn,
  fetchPlans,
  fetchPolicies,
  fetchSubscription,
  pauseSubscription,
  resumeSubscription,
  saveChargePolicy,
  subscriptionsByCustomer,
  saveConversionPolicy,
  saveUsagePolicy,
  updatePlan,
  type ChurnReport,
  type Coverage,
  type Plan,
  type PlanPeriod,
  type Policies,
  type Subscription,
  type SubscriptionCard,
} from './api';
import { CustomerPicker, VehiclePicker } from '../../shell/pickers';
import { billingStrings, type BillingKey, type BillingTranslate } from './strings';
import { RetentionTab, SellTab, VehiclesPanel } from './manage';

const TABS = ['plans', 'sell', 'lookup', 'retention', 'churn', 'policies'] as const;
type Tab = (typeof TABS)[number];

const PERIODS: readonly PlanPeriod[] = ['month', 'quarter', 'year'];

// Формат один на всю админку: раньше здесь тенге печатались под заголовком
// «Цена, тиын», а поле ввода принимало тиыны — расхождение в сто раз на одном экране.
function money(minor: number | string): string {
  const value = typeof minor === 'string' ? Number(minor) : minor;
  return formatMoney(value);
}

function when(value: string | null): string {
  return formatMoment(value);
}

function percent(rate: number): string {
  return `${Math.round(rate * 1000) / 10}%`;
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card border border-line bg-surface shadow-sm shadow-black/[0.03]">
      <h3 className="border-b border-line px-4 py-3 text-sm font-semibold text-ink">{title}</h3>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-card border border-line bg-surface p-4">
      <div className="text-xs tracking-wide text-ink-muted uppercase">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular text-ink">{value}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-ink">{label}</span>
      {children}
    </label>
  );
}

const INPUT =
  'w-full rounded border border-line-strong bg-surface px-2.5 py-1.5 text-sm text-ink outline-none transition focus:border-brand-500 disabled:bg-sunken';
const BUTTON =
  'rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-ink-invert transition hover:bg-brand-700 disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none disabled:cursor-not-allowed';
const GHOST =
  'rounded border border-line-strong px-3 py-1.5 text-sm font-medium text-ink transition hover:bg-sunken disabled:text-ink-faint disabled:cursor-not-allowed';

function statusLabel(t: BillingTranslate, status: string): string {
  return t(`billing.status.${status}` as BillingKey);
}

function PlansTab({ t }: { t: BillingTranslate }) {
  const toast = useToast();
  const [plans, setPlans] = useState<readonly Plan[]>([]);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({
    code: '',
    name: '',
    period: 'month' as PlanPeriod,
    priceMinor: 0,
    usesPerPeriod: '',
    maxVehicles: '',
  });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      setPlans(await fetchPlans());
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function submit(): Promise<void> {
    setBusy(true);
    try {
      const limit = draft.usesPerPeriod.trim();
      // Больше одной машины — семейный план (ТЗ §7 М4.9).
      const vehicles = Number(draft.maxVehicles.trim() || '1');
      const limits = {
        ...(limit ? { usesPerPeriod: Number(limit) } : {}),
        ...(vehicles > 1 ? { maxVehicles: vehicles } : {}),
      };
      await createPlan({
        code: draft.code.trim(),
        name: draft.name.trim(),
        period: draft.period,
        priceMinor: draft.priceMinor,
        ...(Object.keys(limits).length > 0 ? { limits } : {}),
      });
      setDraft({ code: '', name: '', period: 'month', priceMinor: 0, usesPerPeriod: '', maxVehicles: '' });
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
    } finally {
      setBusy(false);
    }
  }

  async function toggle(plan: Plan): Promise<void> {
    setBusy(true);
    try {
      await updatePlan(plan.id, { status: plan.status === 'active' ? 'archived' : 'active' });
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Panel title={t('billing.plans.create')}>
        <div className="grid gap-3 sm:grid-cols-6">
          <Field label={t('billing.plans.code')}>
            <TextInput
              value={draft.code}
              onChange={(e) => setDraft({ ...draft, code: e.target.value })}
            />
          </Field>
          <Field label={t('billing.plans.name')}>
            <TextInput
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            />
          </Field>
          <Field label={t('billing.plans.period')}>
            <Select
              value={draft.period}
              onChange={(e) => setDraft({ ...draft, period: e.target.value as PlanPeriod })}
            >
              {PERIODS.map((p) => (
                <option key={p} value={p}>
                  {t(`billing.period.${p}` as BillingKey)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('billing.plans.price')}>
            <MoneyInput
              valueMinor={draft.priceMinor}
              onChangeMinor={(minor) => setDraft({ ...draft, priceMinor: minor ?? 0 })}
            />
          </Field>
          <Field label={t('billing.plans.limit')}>
            <TextInput
              type="number"
              min={1}
              value={draft.usesPerPeriod}
              onChange={(e) => setDraft({ ...draft, usesPerPeriod: e.target.value })}
            />
          </Field>
          <Field label={t('billing.plans.maxVehicles')}>
            <TextInput
              type="number"
              min={1}
              max={10}
              placeholder="1"
              value={draft.maxVehicles}
              onChange={(e) => setDraft({ ...draft, maxVehicles: e.target.value })}
            />
          </Field>
        </div>
        <div className="mt-3">
          <button
            className={BUTTON}
            disabled={busy || !draft.code.trim() || !draft.name.trim()}
            onClick={() => void submit()}
          >
            {t('billing.plans.create')}
          </button>
        </div>
      </Panel>

      <Panel title={t('billing.plans.title')}>
        {busy && plans.length === 0 ? (
          <TableSkeleton rows={4} columns={6} />
        ) : plans.length === 0 ? (
          <p className="text-sm text-ink-muted">
            {busy ? t('billing.loading') : t('billing.empty')}
          </p>
        ) : (
          <Table>
            <thead className="text-left text-ink-muted">
              <tr>
                <th className="py-1">{t('billing.plans.code')}</th>
                <th>{t('billing.plans.name')}</th>
                <th>{t('billing.plans.period')}</th>
                <th>{t('billing.plans.price')}</th>
                <th>{t('billing.plans.limit')}</th>
                <th>{t('billing.plans.maxVehicles')}</th>
                <th>{t('billing.plans.status')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id} className="border-t border-line">
                  <td className="py-2 font-mono text-xs">{plan.code}</td>
                  <td>{plan.name}</td>
                  <td>{t(`billing.period.${plan.period}` as BillingKey)}</td>
                  <td>{money(plan.priceMinor)}</td>
                  <td>{plan.limits.usesPerPeriod ?? t('billing.plans.unlimited')}</td>
                  <td>{plan.limits.maxVehicles ?? 1}</td>
                  <td>{statusLabel(t, plan.status)}</td>
                  <td className="text-right">
                    <button className={GHOST} disabled={busy} onClick={() => void toggle(plan)}>
                      {plan.status === 'active'
                        ? t('billing.plans.archive')
                        : t('billing.plans.restore')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>
    </div>
  );
}

function LookupTab({ t }: { t: BillingTranslate }) {
  const toast = useToast();
  const [vehicleId, setVehicleId] = useState('');
  const [coverage, setCoverage] = useState<Coverage | undefined>();
  const [subscriptionId, setSubscriptionId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [subs, setSubs] = useState<readonly Subscription[] | null>(null);
  const [card, setCard] = useState<SubscriptionCard | undefined>();
  const [busy, setBusy] = useState(false);

  async function guard(run: () => Promise<void>): Promise<void> {
    setBusy(true);
    try {
      await run();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
    } finally {
      setBusy(false);
    }
  }

  const reload = (id: string) =>
    guard(async () => {
      setCard(await fetchSubscription(id));
    });

  return (
    <div className="space-y-4">
      {/*
        * Заголовок панели обещал проверку «по номеру автомобиля», а поле
        * просило идентификатор — двадцать шесть символов, которых нет ни на
        * машине, ни на экране кассы. Спрашиваем номер и разрешаем его сами.
        */}
      <Panel title={t('billing.lookup.byVehicle')}>
        <div className="w-80">
          <VehiclePicker
            value={vehicleId}
            onChange={(id) => {
              setVehicleId(id);
              setCoverage(undefined);
              if (id) void guard(async () => setCoverage(await checkVehicle(id)));
            }}
          />
        </div>

        {coverage ? (
          <div className="mt-3 space-y-1 text-sm">
            <div className={coverage.covered ? 'text-ok' : 'text-warn'}>
              {coverage.covered ? t('billing.lookup.covered') : t('billing.lookup.notCovered')} —{' '}
              {t(`billing.reason.${coverage.reason}` as BillingKey)}
            </div>
            {/*
              * Остатки — только когда подписка есть. Без неё `usesLeft` приходит
              * пустым и падал в «Без ограничения»: на машине без подписки экран
              * писал «Осталось моек: Без ограничения», то есть ровно обратное
              * строке над ним.
              */}
            {coverage.periodEnd ? (
              <div className="text-ink-muted">
                {t('billing.lookup.usesLeft')}: {coverage.usesLeft ?? t('billing.plans.unlimited')}
                {' · '}
                {t('billing.lookup.credits')}: {coverage.creditsLeft}
                {' · '}
                {t('billing.lookup.periodEnd')}: {when(coverage.periodEnd)}
              </div>
            ) : null}
            {coverage.nextAllowedAt ? (
              <div className="text-ink-muted">
                {t('billing.lookup.nextAllowed')}: {when(coverage.nextAllowedAt)}
              </div>
            ) : null}
          </div>
        ) : null}
      </Panel>

      {/*
        * Поиск по клиенту. До него открыть карточку можно было, только зная
        * идентификатор подписки или автомобиля — то есть уже держа в руках то,
        * что ищешь.
        */}
      <Panel title={t('billing.lookup.byCustomer')}>
        <div className="w-80">
          <CustomerPicker
            value={customerId}
            onChange={(id) => {
              setCustomerId(id);
              setSubs(null);
              if (id) {
                void guard(async () => {
                  setSubs(await subscriptionsByCustomer(id));
                });
              }
            }}
          />
        </div>

        {subs === null ? null : subs.length === 0 ? (
          <p className="mt-3 text-sm text-ink-muted">{t('billing.lookup.noSubs')}</p>
        ) : (
          <ul className="mt-3 flex flex-col gap-1">
            {subs.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className="w-full rounded border border-line px-3 py-2 text-left text-sm hover:bg-sunken"
                  onClick={() => {
                    setSubscriptionId(s.id);
                    void reload(s.id);
                  }}
                >
                  <span className="font-medium text-ink">{s.planCode}</span>
                  <span className="ml-2 text-xs text-ink-muted">
                    {statusLabel(t, s.status)} · {t('billing.card.periodEnd')}:{' '}
                    {when(s.currentPeriodEnd)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title={t('billing.lookup.byId')}>
        <p className="mb-2 text-sm text-ink-muted">{t('billing.lookup.idHint')}</p>
        <div className="flex gap-2">
          <TextInput
            placeholder={t('billing.lookup.idPlaceholder')}
            value={subscriptionId}
            onChange={(e) => setSubscriptionId(e.target.value)}
          />
          <button
            className={BUTTON}
            disabled={busy || !subscriptionId.trim()}
            onClick={() => void reload(subscriptionId.trim())}
          >
            {t('billing.lookup.find')}
          </button>
        </div>
      </Panel>

      {card ? (
        <Panel title={t('billing.card.title')}>
          <div className="grid gap-2 sm:grid-cols-4">
            <Tile label={t('billing.card.plan')} value={card.subscription.planCode} />
            <Tile
              label={t('billing.card.status')}
              value={statusLabel(t, card.subscription.status)}
            />
            <Tile
              label={t('billing.card.periodEnd')}
              value={when(card.subscription.currentPeriodEnd)}
            />
            <Tile
              label={t('billing.card.autorenew')}
              value={card.subscription.autorenew ? '✓' : '—'}
            />
          </div>

          <div className="mt-3 flex gap-2">
            {/*
              * Ручное списание. Маршрут был с самого начала — им пользуются,
              * когда автосписание упёрлось в отказ банка и клиент оплатил
              * иначе. Кнопки не было, и запускали его curl-ом.
              */}
            <button
              className={GHOST}
              disabled={busy || !['active', 'past_due'].includes(card.subscription.status)}
              onClick={() =>
                void guard(async () => {
                  await chargeSubscription(card.subscription.id);
                  setCard(await fetchSubscription(card.subscription.id));
                  toast.ok(t('billing.card.charged'));
                })
              }
            >
              {t('billing.card.charge')}
            </button>
            <button
              className={GHOST}
              disabled={busy || card.subscription.status !== 'active'}
              onClick={() =>
                void guard(async () => {
                  await pauseSubscription(card.subscription.id);
                  setCard(await fetchSubscription(card.subscription.id));
                })
              }
            >
              {t('billing.card.pause')}
            </button>
            <button
              className={GHOST}
              disabled={busy || card.subscription.status !== 'paused'}
              onClick={() =>
                void guard(async () => {
                  await resumeSubscription(card.subscription.id);
                  setCard(await fetchSubscription(card.subscription.id));
                })
              }
            >
              {t('billing.card.resume')}
            </button>
            <button
              className={GHOST}
              disabled={
                busy ||
                card.subscription.status === 'cancelled' ||
                card.subscription.status === 'expired'
              }
              onClick={() =>
                void guard(async () => {
                  await cancelSubscription(card.subscription.id, 'operator_request');
                  setCard(await fetchSubscription(card.subscription.id));
                })
              }
            >
              {t('billing.card.cancelSub')}
            </button>
          </div>

          <VehiclesPanel
            t={t}
            subscriptionId={card.subscription.id}
            onChanged={() => void reload(card.subscription.id)}
          />

          <h4 className="mt-4 text-sm font-semibold text-ink">{t('billing.card.charges')}</h4>
          <Table className="mt-1">
            <thead className="text-left text-ink-muted">
              <tr>
                <th className="py-1">{t('billing.card.chargePeriod')}</th>
                <th>{t('billing.card.chargeAttempt')}</th>
                <th>{t('billing.card.chargeStatus')}</th>
                <th>{t('billing.card.chargeReason')}</th>
                <th>{t('billing.card.chargeNext')}</th>
              </tr>
            </thead>
            <tbody>
              {card.charges.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="py-1 font-mono text-xs">{row.period_key}</td>
                  <td>{row.attempt}</td>
                  <td>{row.status}</td>
                  <td>{row.failure_reason ?? '—'}</td>
                  <td>{when(row.next_attempt_at)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          <h4 className="mt-4 text-sm font-semibold text-ink">{t('billing.card.transitions')}</h4>
          <Table className="mt-1">
            <thead className="text-left text-ink-muted">
              <tr>
                <th className="py-1">{t('billing.card.transitionFrom')}</th>
                <th>{t('billing.card.transitionTo')}</th>
                <th>{t('billing.card.transitionWhen')}</th>
              </tr>
            </thead>
            <tbody>
              {card.transitions.map((row, i) => (
                <tr key={`${row.created_at}-${i}`} className="border-t border-line">
                  <td className="py-1">{statusLabel(t, row.from_status)}</td>
                  <td>{statusLabel(t, row.to_status)}</td>
                  <td>{when(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Panel>
      ) : null}
    </div>
  );
}

function ChurnTab({ t }: { t: BillingTranslate }) {
  const toast = useToast();
  const today = new Date();
  const monthAgo = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 3, 1));

  const [from, setFrom] = useState(monthAgo.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [report, setReport] = useState<ChurnReport | undefined>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      setReport(await fetchChurn(new Date(from).toISOString(), new Date(to).toISOString()));
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
    } finally {
      setBusy(false);
    }
  }, [from, to, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <Field label={t('billing.churn.from')}>
          <TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label={t('billing.churn.to')}>
          <TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <button className={BUTTON} disabled={busy} onClick={() => void load()}>
          {t('billing.refresh')}
        </button>
      </div>

      {report ? (
        <>
          {/* «Истекло» замыкает баланс: на начало + подключения − отписки − истекшие
              = на конец. Без этой плитки 10 + 10 − 4 не давало 14 (QA.md, Q11). */}
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Tile label={t('billing.churn.activeAtStart')} value={String(report.activeAtStart)} />
            <Tile label={t('billing.churn.activated')} value={String(report.activated)} />
            <Tile label={t('billing.churn.cancelled')} value={String(report.cancelled)} />
            <Tile label={t('billing.churn.expired')} value={String(report.expired)} />
            <Tile label={t('billing.churn.rate')} value={percent(report.churnRate)} />
            <Tile label={t('billing.churn.activeAtEnd')} value={String(report.activeAtEnd)} />
          </div>

          <p className="text-xs text-ink-muted">{t('billing.churn.definition')}</p>

          <Panel title={t('billing.churn.byMonth')}>
            <Table>
              <thead className="text-left text-ink-muted">
                <tr>
                  <th className="py-1">{t('billing.churn.month')}</th>
                  <th>{t('billing.churn.activeAtStart')}</th>
                  <th>{t('billing.churn.activated')}</th>
                  <th>{t('billing.churn.cancelled')}</th>
                  <th>{t('billing.churn.rate')}</th>
                </tr>
              </thead>
              <tbody>
                {report.buckets.map((b) => (
                  <tr key={b.month} className="border-t border-line">
                    <td className="py-1 font-mono text-xs">{b.month}</td>
                    <td>{b.activeAtStart}</td>
                    <td>{b.activated}</td>
                    <td>{b.cancelled}</td>
                    <td>{percent(b.churnRate)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Panel>

          <Panel title={t('billing.churn.byPlan')}>
            <Table>
              <thead className="text-left text-ink-muted">
                <tr>
                  <th className="py-1">{t('billing.plans.code')}</th>
                  <th>{t('billing.churn.activeAtStart')}</th>
                  <th>{t('billing.churn.activated')}</th>
                  <th>{t('billing.churn.cancelled')}</th>
                  <th>{t('billing.churn.rate')}</th>
                </tr>
              </thead>
              <tbody>
                {report.byPlan.map((p) => (
                  <tr key={p.planId} className="border-t border-line">
                    <td className="py-1 font-mono text-xs">{p.planCode}</td>
                    <td>{p.activeAtStart}</td>
                    <td>{p.activated}</td>
                    <td>{p.cancelled}</td>
                    <td>{percent(p.churnRate)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Panel>
        </>
      ) : null}
    </div>
  );
}

interface ChargeDraft {
  readonly maxAttempts: number;
  readonly retryIntervalHours: readonly number[];
  readonly gracePeriodHours: number;
  readonly reminderLeadDays: number;
}

function parseHours(value: string): readonly number[] {
  return value
    .split(',')
    .map((v) => Number(v.trim()))
    .filter((v) => Number.isFinite(v) && v > 0);
}

/**
 * Вкладка настроек списания. Раньше на ней не было ни одной кнопки: десять полей
 * сохранялись по потере фокуса, и «Число попыток» менялось от одного клика в
 * соседнее поле — на настройках, управляющих списанием денег. Теперь изменения
 * копятся в черновике и уходят по явному «Сохранить».
 *
 * Второе: поле попыток допускало до 10, а сервер принимает не больше, чем
 * интервалов повтора плюс один. Расхождение вылезало как HTTP 500. Верхняя
 * граница теперь считается от самих интервалов и подписана рядом.
 */
function PoliciesTab({ t }: { t: BillingTranslate }) {
  const toast = useToast();
  const [policies, setPolicies] = useState<Policies | undefined>();
  const [charge, setCharge] = useState<ChargeDraft | undefined>();
  const [intervalsText, setIntervalsText] = useState('');
  const [failure, setFailure] = useState<ResourceError | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setFailure(undefined);
    try {
      const loaded = await fetchPolicies();
      setPolicies(loaded);
      setCharge({
        maxAttempts: loaded.charge.maxAttempts,
        retryIntervalHours: loaded.charge.retryIntervalHours,
        gracePeriodHours: loaded.charge.gracePeriodHours,
        reminderLeadDays: loaded.charge.reminderLeadDays,
      });
      setIntervalsText(loaded.charge.retryIntervalHours.join(', '));
    } catch (err) {
      // Ветка ошибки обязана сама снять «Загрузка…», иначе экран висит навсегда.
      setFailure(toResourceError(err));
      toast.error(
        t('billing.policies.charge'),
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setBusy(false);
    }
  }, [toast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function guard(run: () => Promise<unknown>, okText: string): Promise<void> {
    setBusy(true);
    try {
      await run();
      await load();
      toast.ok(okText);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : String(err),
        err instanceof ApiError ? err.code : undefined,
      );
    } finally {
      setBusy(false);
    }
  }

  if (failure) return <ErrorState error={failure} onRetry={() => void load()} />;
  if (!policies || !charge) return <LoadingState label={t('billing.loading')} />;

  const intervals = parseHours(intervalsText);
  const maxAttemptsAllowed = Math.max(intervals.length + 1, 1);
  const attemptsTooHigh = charge.maxAttempts > maxAttemptsAllowed;
  const chargeDirty =
    charge.maxAttempts !== policies.charge.maxAttempts ||
    intervals.join(',') !== policies.charge.retryIntervalHours.join(',') ||
    charge.gracePeriodHours !== policies.charge.gracePeriodHours ||
    charge.reminderLeadDays !== policies.charge.reminderLeadDays;

  return (
    <div className="space-y-4">
      <Panel title={t('billing.policies.charge')}>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label={t('billing.policies.maxAttempts')}>
            <TextInput
              type="number"
              min={1}
              max={maxAttemptsAllowed}
              value={charge.maxAttempts}
              onChange={(e) => setCharge({ ...charge, maxAttempts: Number(e.target.value) })}
            />
          </Field>
          <Field label={t('billing.policies.intervals')}>
            <TextInput value={intervalsText} onChange={(e) => setIntervalsText(e.target.value)} />
          </Field>
          <Field label={t('billing.policies.grace')}>
            <TextInput
              type="number"
              min={0}
              value={charge.gracePeriodHours}
              onChange={(e) => setCharge({ ...charge, gracePeriodHours: Number(e.target.value) })}
            />
          </Field>
          <Field label={t('billing.policies.reminder')}>
            <TextInput
              type="number"
              min={0}
              value={charge.reminderLeadDays}
              onChange={(e) => setCharge({ ...charge, reminderLeadDays: Number(e.target.value) })}
            />
          </Field>
        </div>

        <p
          className={`mt-2 text-xs ${attemptsTooHigh ? 'font-medium text-danger' : 'text-ink-muted'}`}
        >
          {t('billing.policies.attemptsHint', {
            intervals: intervals.length,
            max: maxAttemptsAllowed,
          })}
        </p>
        <p className="mt-1 text-xs text-ink-muted">{t('billing.policies.intervalsHint')}</p>

        <div className="mt-3 flex items-center gap-3">
          <button
            className={BUTTON}
            disabled={busy || !chargeDirty || attemptsTooHigh}
            onClick={() =>
              void guard(
                () =>
                  saveChargePolicy({
                    maxAttempts: charge.maxAttempts,
                    retryIntervalHours: intervals,
                    gracePeriodHours: charge.gracePeriodHours,
                    reminderLeadDays: charge.reminderLeadDays,
                  }),
                t('billing.policies.saved'),
              )
            }
          >
            {t('billing.policies.save')}
          </button>
          {chargeDirty ? (
            <span className="text-xs text-warn">{t('billing.policies.dirty')}</span>
          ) : null}
        </div>
      </Panel>

      <Panel title={t('billing.policies.usage')}>
        <div className="grid gap-3 sm:grid-cols-4">
          <Field label={t('billing.policies.perPeriod')}>
            <TextInput
              type="number"
              min={1}
              defaultValue={policies.usage.maxUsesPerPeriod ?? ''}
              onBlur={(e) =>
                void guard(
                  () =>
                    saveUsagePolicy({
                      maxUsesPerPeriod: e.target.value ? Number(e.target.value) : null,
                    }),
                  t('billing.policies.saved'),
                )
              }
            />
          </Field>
          <Field label={t('billing.policies.perDay')}>
            <TextInput
              type="number"
              min={1}
              defaultValue={policies.usage.maxUsesPerDay}
              onBlur={(e) =>
                void guard(
                  () => saveUsagePolicy({ maxUsesPerDay: Number(e.target.value) }),
                  t('billing.policies.saved'),
                )
              }
            />
          </Field>
          <Field label={t('billing.policies.minHours')}>
            <TextInput
              type="number"
              min={0}
              defaultValue={policies.usage.minHoursBetweenUses}
              onBlur={(e) =>
                void guard(
                  () => saveUsagePolicy({ minHoursBetweenUses: Number(e.target.value) }),
                  t('billing.policies.saved'),
                )
              }
            />
          </Field>
          <Field label={t('billing.policies.vehicle')}>
            <Checkbox
              defaultChecked={policies.usage.enforceVehicleBinding}
              onChange={(e) =>
                void guard(
                  () => saveUsagePolicy({ enforceVehicleBinding: e.target.checked }),
                  t('billing.policies.saved'),
                )
              }
              className="mt-2"
            />
          </Field>
        </div>
      </Panel>

      <Panel title={t('billing.policies.conversion')}>
        <Field label={t('billing.policies.strategy')}>
          <Select
            value={policies.conversion.strategy}
            disabled={busy}
            onChange={(e) =>
              void guard(
                () => saveConversionPolicy(e.target.value as Policies['conversion']['strategy']),
                t('billing.policies.saved'),
              )
            }
          >
            <option value="carry_over">{t('billing.policies.carry_over')}</option>
            <option value="extend_period">{t('billing.policies.extend_period')}</option>
            <option value="customer_choice">{t('billing.policies.customer_choice')}</option>
          </Select>
        </Field>
        <p className="mt-2 text-xs text-ink-muted">{t('billing.policies.conversionNote')}</p>
      </Panel>
    </div>
  );
}

export default function BillingModule({ user, title }: ModuleViewProps) {
  const t = billingStrings(user.locale);
  // Вкладка живёт в адресе: F5 больше не сбрасывает на первую.
  const [tab, setTab] = useTab<Tab>('billing', TABS, 'plans');

  return (
    <ModulePage
      title={title}
      subtitle={t('billing.subtitle')}
      tabs={TABS.map((key) => ({ key, label: t(`billing.tab.${key}` as BillingKey) }))}
      activeTab={tab}
      onTab={setTab}
    >
      {tab === 'plans' ? <PlansTab t={t} /> : null}
      {tab === 'sell' ? <SellTab t={t} /> : null}
      {tab === 'lookup' ? <LookupTab t={t} /> : null}
      {tab === 'retention' ? <RetentionTab t={t} /> : null}
      {tab === 'churn' ? <ChurnTab t={t} /> : null}
      {tab === 'policies' ? <PoliciesTab t={t} /> : null}
    </ModulePage>
  );
}
